import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './SellReturnModal.css';
import apiService from '../../services/api';

const SellReturnModal = ({ sale, locations = [], onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    invoiceNo: '',
    date: (() => { const d = new Date(); const pad = n => String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; })(),
    discountType: 'none',
    discountAmount: '0.00'
  });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({
    returnDiscount: 0,
    returnTax: 0,
    returnTotal: 0
  });

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!sale) return;
      
      setLoading(true);
      try {
        const apiData = sale.apiData || sale;
        const saleProducts = apiData.sell_lines || sale.products || [];
        
        console.log('=== SELL RETURN MODAL - PRODUCT DATA ===');
        console.log('Total products in sale:', saleProducts.length);
        
        // Fetch complete product details for each sell line
        const productsWithDetails = await Promise.all(
          saleProducts.map(async (product, index) => {
            console.log(`\n--- Product ${index + 1} ---`);
            console.log('Raw sell_line data:', product);
            console.log('product_id:', product.product_id);
            console.log('variation_id:', product.variation_id);
            console.log('unit_price_inc_tax:', product.unit_price_inc_tax);
            console.log('unit_price:', product.unit_price);
            console.log('quantity:', product.quantity);
            
            let productName = product.product?.name || product.product_name || product.name;
            let productSku = product.product?.sku || product.sku;
            
            // If product details are missing, fetch from API
            if (!productName || productName.startsWith('Product #') || !productSku || productSku === '--') {
              try {
                const productId = product.product_id;
                if (productId) {
                  const productResponse = await apiService.getProduct(productId);
                  const productData = productResponse.data || productResponse;
                  const fetchedProduct = Array.isArray(productData) ? productData[0] : productData.data || productData;
                  
                  if (fetchedProduct) {
                    productName = fetchedProduct.name || productName;
                    productSku = fetchedProduct.sku || productSku;
                  }
                }
              } catch (error) {
                console.error(`Failed to fetch product ${product.product_id}:`, error);
              }
            }
            
            return {
              id: product.id,
              productId: product.product_id,
              variationId: product.variation_id,
              name: productName || `Product #${product.product_id || product.id}`,
              sku: productSku || '--',
              unitPrice: parseFloat(product.unit_price_inc_tax || product.unit_price || product.unitPrice || 0),
              sellQuantity: parseFloat(product.quantity || 0),
              returnQuantity: 0,
              returnSubtotal: 0
            };
          })
        );
        
        setProducts(productsWithDetails);
      } catch (error) {
        console.error('Error fetching product details:', error);
        // Fallback to basic data if fetch fails
        const apiData = sale.apiData || sale;
        const saleProducts = apiData.sell_lines || sale.products || [];
        const productsWithReturn = saleProducts.map(product => ({
          id: product.id,
          productId: product.product_id,
          variationId: product.variation_id,
          name: product.product?.name || product.variations?.name || product.product_name || product.name || `Product #${product.product_id || product.id}`,
          sku: product.product?.sku || product.variations?.sub_sku || product.sku || '--',
          unitPrice: parseFloat(product.unit_price_inc_tax || product.unit_price || product.unitPrice || 0),
          sellQuantity: parseFloat(product.quantity || 0),
          returnQuantity: 0,
          returnSubtotal: 0
        }));
        setProducts(productsWithReturn);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductDetails();
  }, [sale]);

  useEffect(() => {
    calculateTotals();
  }, [products, formData.discountType, formData.discountAmount]);

  const handleReturnQuantityChange = (index, value) => {
    const newProducts = [...products];
    const quantity = parseFloat(value) || 0;
    const maxQuantity = newProducts[index].sellQuantity;

    // Ensure return quantity doesn't exceed sell quantity
    newProducts[index].returnQuantity = Math.min(quantity, maxQuantity);
    newProducts[index].returnSubtotal = newProducts[index].returnQuantity * newProducts[index].unitPrice;

    setProducts(newProducts);
  };

  const calculateTotals = () => {
    const subtotal = products.reduce((sum, p) => sum + p.returnSubtotal, 0);

    let discount = 0;
    if (formData.discountType === 'fixed') {
      discount = parseFloat(formData.discountAmount) || 0;
    } else if (formData.discountType === 'percentage') {
      discount = (subtotal * (parseFloat(formData.discountAmount) || 0)) / 100;
    }

    const tax = 0; // Calculate based on tax rules if needed
    const total = subtotal - discount + tax;

    setTotals({
      returnDiscount: discount,
      returnTax: tax,
      returnTotal: total
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate at least one product has return quantity
    const hasReturns = products.some(p => p.returnQuantity > 0);
    if (!hasReturns) {
      alert('Please enter return quantity for at least one product.');
      return;
    }

    try {
      console.log('=== SELL RETURN SUBMISSION DEBUG ===');
      console.log('Sale object:', sale);
      console.log('Sale ID:', sale.id);
      console.log('Sale apiData:', sale.apiData);
      console.log('Transaction ID to use:', sale.apiData?.id || sale.id);
      
      const rawDate = formData.date || '';
      const [datePart, timePart = '00:00'] = rawDate.split('T');
      const timeWithSeconds = timePart.length === 5 ? `${timePart}:00` : timePart.slice(0, 8);
      const formattedDate = `${datePart} ${timeWithSeconds}`;

      const apiDiscountType = formData.discountType === 'none' ? 'fixed' : formData.discountType;
      const apiDiscountAmount = formData.discountType === 'none' ? 0 : (parseFloat(formData.discountAmount) || 0);

      // Use the correct transaction ID from apiData if available
      const transactionId = parseInt(sale.apiData?.id || sale.id, 10);
      console.log('Using transaction_id:', transactionId);

      const returnData = {
        transaction_id: transactionId,
        transaction_date: formattedDate,
        tax_id: 1,
        discount_type: apiDiscountType,
        discount_amount: apiDiscountAmount,
        products: products.filter(p => p.returnQuantity > 0).map(p => ({
          sell_line_id: parseInt(p.id, 10),
          product_id: parseInt(p.productId, 10),
          variation_id: p.variationId ? parseInt(p.variationId, 10) : null,
          quantity: parseFloat(p.returnQuantity),
          unit_price_inc_tax: parseFloat(p.unitPrice)
        })),
        payments: [
          {
            amount: parseFloat(totals.returnTotal.toFixed(2)),
            method: 'credit',
            note: 'Credit for returned items'
          }
        ]
      };

      if (formData.invoiceNo) returnData.invoice_no = formData.invoiceNo;

      console.log('=== SUBMITTING SELL RETURN ===');
      console.log('PAYLOAD:', JSON.stringify(returnData, null, 2));
      console.log('Products being returned:');
      returnData.products.forEach((p, i) => {
        console.log(`  ${i + 1}. sell_line_id: ${p.sell_line_id}, product_id: ${p.product_id}, variation_id: ${p.variation_id}, qty: ${p.quantity}`);
      });
      
      await apiService.createSellReturn(returnData);
      alert('Sell return created successfully!');
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating sell return:', error);
      alert(`Failed to create sell return: ${error.message}`);
    }
  };

  if (!sale) return null;

  const apiData = sale.apiData || sale;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container sell-return-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Sell Return</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content sell-return-content">
            {/* Parent Sale Info */}
            <div className="parent-sale-section">
              <h3>Parent Sale</h3>
              <div className="parent-sale-grid">
                <div className="info-item">
                  <strong>Invoice No.:</strong> {apiData.invoice_no || sale.invoiceNo}
                </div>
                <div className="info-item">
                  <strong>Date:</strong> {new Date(apiData.transaction_date || sale.saleDate).toLocaleDateString()}
                </div>
                <div className="info-item">
                  <strong>Customer:</strong> {apiData.contact?.name || sale.customer}
                </div>
                <div className="info-item">
                  <strong>Business Location:</strong> {apiData.location?.name || locations.find(l => l.id === parseInt(apiData.location_id))?.name || sale.location}
                </div>
              </div>
            </div>

            {/* Return Form */}
            <div className="return-form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>Invoice No.:</label>
                  <input
                    type="text"
                    value={formData.invoiceNo}
                    onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })}
                    placeholder="Auto-generated"
                  />
                </div>
                <div className="form-group">
                  <label>Date:*</label>
                  <input
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Products Table */}
            <div className="products-return-section">
              <div className="table-wrapper">
                <table className="return-products-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product Name</th>
                      <th>Unit Price</th>
                      <th>Sell Quantity</th>
                      <th>Return Quantity</th>
                      <th>Return Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>
                          ⏳ Loading product details...
                        </td>
                      </tr>
                    ) : products.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--etpos-text-muted)' }}>
                          No products found
                        </td>
                      </tr>
                    ) : products.map((product, index) => (
                      <tr key={product.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div>{product.name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>
                            {product.sku}
                          </div>
                        </td>
                        <td>${product.unitPrice.toFixed(2)}</td>
                        <td>{product.sellQuantity.toFixed(2)} Single</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={product.sellQuantity}
                            step="0.01"
                            value={product.returnQuantity}
                            onChange={(e) => handleReturnQuantityChange(index, e.target.value)}
                            className="return-quantity-input"
                          />
                        </td>
                        <td>${product.returnSubtotal.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Discount and Totals */}
            <div className="discount-totals-section">
              <div className="discount-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Discount Type:</label>
                    <select
                      value={formData.discountType}
                      onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    >
                      <option value="none">None</option>
                      <option value="fixed">Fixed</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Discount Amount:</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.discountAmount}
                      onChange={(e) => setFormData({ ...formData, discountAmount: e.target.value })}
                      disabled={formData.discountType === 'none'}
                    />
                  </div>
                </div>
              </div>

              <div className="totals-display">
                <div className="total-row">
                  <span>Total Return Discount:</span>
                  <span>(-) ${totals.returnDiscount.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Total Return Tax:</span>
                  <span>(+) ${totals.returnTax.toFixed(2)}</span>
                </div>
                <div className="total-row final-total">
                  <span>Return Total:</span>
                  <span>${totals.returnTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Close
            </button>
            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellReturnModal;
